import React from 'react';
import { motion } from 'framer-motion';
import { Settings, TrendingUp, Shield, Check, ArrowRight } from 'lucide-react';

const ExpertiseSection = () => {
  const services = [
    {
      id: 1,
      icon: Settings,
      title: "Operational Excellence",
      subtitle: "Optimize Your Restaurant Operations",
      items: [
        "Process & Productivity Improvement",
        "Menu Engineering & Innovation",
        "Kitchen Performance Optimization",
        "Inventory & Cost Control"
      ],
      bgColor: "#264653",
      gradient: "linear-gradient(135deg, #264653 0%, #1a3240 100%)"
    },
    {
      id: 2,
      icon: TrendingUp,
      title: "Growth & Visibility",
      subtitle: "Expand Your Brand Reach",
      items: [
        "Branding & Marketing",
        "Digital Presence Management",
        "Delivery Revenue Management",
        "Customer Relationship Management (CRM)",
        "Catering / Events Management"
      ],
      bgColor: "#2a9d8f",
      gradient: "linear-gradient(135deg, #2a9d8f 0%, #1e7468 100%)"
    },
    {
      id: 3,
      icon: Shield,
      title: "Management & Sustainability",
      subtitle: "Build Long-term Success",
      items: [
        "Financial Control & Reporting",
        "Team Performance & Development",
        "Infrastructure & Asset Management"
      ],
      bgColor: "#e76f51",
      gradient: "linear-gradient(135deg, #e76f51 0%, #d55a3a 100%)"
    }
  ];

  // Container animation for staggered children
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
        delayChildren: 0.3
      }
    }
  };

  // Card animation variants
  const cardVariants = {
    hidden: { 
      opacity: 0, 
      y: 60,
      scale: 0.95
    },
    visible: { 
      opacity: 1, 
      y: 0,
      scale: 1,
      transition: {
        duration: 0.7,
        ease: [0.22, 1, 0.36, 1]
      }
    }
  };

  // List item animation
  const itemVariants = {
    hidden: { opacity: 0, x: -20 },
    visible: (index) => ({
      opacity: 1,
      x: 0,
      transition: {
        delay: index * 0.1,
        duration: 0.5
      }
    })
  };

  return (
    <section className="relative py-24 md:py-32 px-6 bg-[#F6F4EF] overflow-hidden">
      {/* Subtle Background Pattern */}
      <div className="absolute inset-0 opacity-[0.02]">
        <div className="absolute top-0 left-0 w-full h-full" 
          style={{
            backgroundImage: `radial-gradient(circle at 2px 2px, #1b7840 1px, transparent 0)`,
            backgroundSize: '48px 48px'
          }}
        />
      </div>

      {/* Decorative Background Blobs */}
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.05, scale: 1 }}
        transition={{ duration: 1.2 }}
        viewport={{ once: true }}
        className="absolute top-1/4 left-10 w-96 h-96 rounded-full bg-primary blur-3xl"
      />
      <motion.div
        initial={{ opacity: 0, scale: 0.8 }}
        whileInView={{ opacity: 0.05, scale: 1 }}
        transition={{ duration: 1.2, delay: 0.3 }}
        viewport={{ once: true }}
        className="absolute bottom-1/4 right-10 w-96 h-96 rounded-full bg-orange-400 blur-3xl"
      />

      <div className="max-w-7xl mx-auto relative z-10">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
          viewport={{ once: true, margin: "-100px" }}
          className="text-center mb-20 md:mb-24"
        >
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
            className="inline-block mb-4"
          >
            <span className="text-xs md:text-sm font-bold text-primary uppercase tracking-wider">
              Our Expertise
            </span>
          </motion.div>

          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-extrabold text-gray-900 leading-[1.1] mb-6"
            style={{ letterSpacing: '-0.02em' }}
          >
            End-to-End
            <br />
            <span className="relative inline-block">
              <span className="relative z-10 italic font-serif" style={{ color: "#1b7840" }}>Structured</span>
              <svg
                className="absolute -bottom-2 left-0 w-full h-3"
                viewBox="0 0 200 12"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
                preserveAspectRatio="none"
              >
                <motion.path
                  d="M2 8C50 2, 150 2, 198 8"
                  stroke="#1b7840"
                  strokeWidth="4"
                  strokeLinecap="round"
                  initial={{ pathLength: 0 }}
                  whileInView={{ pathLength: 1 }}
                  transition={{ duration: 1, delay: 0.6 }}
                  viewport={{ once: true }}
                />
              </svg>
            </span>{" "}
            Consulting
          </h2>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            viewport={{ once: true }}
            className="text-lg md:text-xl text-gray-600 max-w-3xl mx-auto"
          >
            Comprehensive solutions designed to transform your restaurant—<br className="hidden md:block" />across operations, growth, and long-term sustainability
          </motion.p>
        </motion.div>

        {/* Services Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 md:gap-10"
        >
          {services.map((service, serviceIndex) => {
            const Icon = service.icon;
            
            return (
              <motion.div
                key={service.id}
                variants={cardVariants}
                whileHover={{ 
                  y: -8,
                  transition: { duration: 0.3 }
                }}
                className="group relative bg-white rounded-3xl shadow-lg hover:shadow-2xl transition-all duration-500 overflow-hidden"
              >
                {/* Gradient overlay on hover */}
                <motion.div
                  className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
                  style={{ 
                    background: `linear-gradient(135deg, ${service.bgColor}05 0%, transparent 100%)` 
                  }}
                />

                {/* Content */}
                <div className="relative p-8 md:p-10">
                  {/* Icon Container with Animation */}
                  <motion.div
                    whileHover={{ 
                      scale: 1.1,
                      rotate: [0, -5, 5, 0],
                      transition: { duration: 0.5 }
                    }}
                    className="relative mb-6 inline-block"
                  >
                    <div 
                      className="w-20 h-20 rounded-2xl flex items-center justify-center relative overflow-hidden"
                      style={{ backgroundColor: `${service.bgColor}15` }}
                    >
                      {/* Shine effect */}
                      <motion.div
                        className="absolute inset-0 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                        initial={{ x: '-100%' }}
                        whileHover={{ x: '100%' }}
                        transition={{ duration: 0.6 }}
                      />
                      <Icon 
                        className="w-10 h-10 relative z-10"
                        style={{ color: service.bgColor }}
                        strokeWidth={1.5}
                      />
                    </div>

                    {/* Glow effect on hover */}
                    <motion.div
                      className="absolute inset-0 rounded-2xl blur-xl -z-10"
                      style={{ backgroundColor: service.bgColor }}
                      initial={{ opacity: 0 }}
                      whileHover={{ opacity: 0.2 }}
                      transition={{ duration: 0.3 }}
                    />
                  </motion.div>

                  {/* Title — forced to two lines so all cards stay aligned */}
                  <motion.h3
                    className="text-2xl md:text-3xl font-bold text-gray-900 mb-3 leading-tight whitespace-pre-line min-h-[2.4em]"
                    style={{ letterSpacing: '-0.01em' }}
                  >
                    {service.title.replace(" & ", " &\n")}
                  </motion.h3>
                  
                  {/* Subtitle */}
                  <p className="text-gray-600 text-base mb-8">
                    {service.subtitle}
                  </p>

                  {/* Items List with Staggered Animation */}
                  <ul className="space-y-4">
                    {service.items.map((item, index) => (
                      <motion.li
                        key={index}
                        custom={index}
                        initial="hidden"
                        whileInView="visible"
                        viewport={{ once: true }}
                        variants={itemVariants}
                        className="flex items-start gap-3 text-gray-700 group/item"
                      >
                        <motion.div
                          whileHover={{ scale: 1.2, rotate: 360 }}
                          transition={{ duration: 0.5 }}
                          className="flex-shrink-0 mt-0.5"
                        >
                          <div
                            className="w-5 h-5 rounded-full flex items-center justify-center"
                            style={{ backgroundColor: `${service.bgColor}15` }}
                          >
                            <Check 
                              className="w-3 h-3"
                              style={{ color: service.bgColor }}
                              strokeWidth={3}
                            />
                          </div>
                        </motion.div>
                        <span className="text-sm md:text-base leading-relaxed group-hover/item:text-gray-900 transition-colors">
                          {item}
                        </span>
                      </motion.li>
                    ))}
                  </ul>

                  {/* Learn More Link */}
                  <motion.div
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ delay: 0.5 }}
                    className="mt-8 pt-6 border-t border-gray-100"
                  >
                    <motion.a
                      href="/services"
                      className="inline-flex items-center gap-2 text-sm font-semibold group/link"
                      style={{ color: service.bgColor }}
                      whileHover={{ x: 5 }}
                    >
                      <span>Learn More</span>
                      <ArrowRight className="w-4 h-4 group-hover/link:translate-x-1 transition-transform" />
                    </motion.a>
                  </motion.div>
                </div>

                {/* Bottom accent line */}
                <motion.div
                  className="absolute bottom-0 left-0 h-1 w-full"
                  style={{ background: service.gradient }}
                  initial={{ scaleX: 0 }}
                  whileInView={{ scaleX: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.8, delay: serviceIndex * 0.2 }}
                />
              </motion.div>
            );
          })}
        </motion.div>

        {/* Bottom CTA */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          viewport={{ once: true }}
          className="text-center mt-20 md:mt-24"
        >
          <motion.p
            className="text-gray-600 text-lg mb-8"
          >
            Ready to transform your restaurant business?
          </motion.p>
          
          <motion.div
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <a
              href="/contact"
              className="inline-flex items-center gap-2 px-10 py-4 bg-primary hover:bg-primary-dark text-white rounded-full font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300"
            >
              Schedule Free Consultation
              <ArrowRight className="w-5 h-5" />
            </a>
          </motion.div>
        </motion.div>
      </div>
    </section>
  );
};

export default ExpertiseSection;